/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn } from '@bem-react/classname'
// import { Tooltip } from 'devextreme-react/tooltip'
import { FC, useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import SelectBox, { components } from 'react-select'
import { Transition } from 'react-transition-group'
import { v4 as uuidv4 } from 'uuid'

import './Select.scss'

const cnSelect = cn('Select')

type OptionType = { [key: string]: any }
type OptionsType = Array<OptionType>
type SelectType = 'simple' | 'filter'
type LabelColorType = 'dark' | 'blue'
type FlexDirectionType = 'column' | 'row'
type alignItemsType = 'top' | 'bottom' | 'right' | 'left' | 'middle'

export interface SelectSchema {
  uid?: string
  label?: string
  name: string
  errors?: string
  placeholder?: string
  required?: boolean
  readonly?: boolean
  disabled?: boolean
  className?: string
  classNameLabel?: string
  options: OptionsType
  optionValue?: string
  optionLabel?: string
  optionImageUrl?: string
  optionImageAlt?: string
  optionBadgeType?: string
  isMulti?: boolean
  isSearchable?: boolean
  noOptionsMessage?: ({ inputValue }: any) => string | null
  control?: any
  defaultValue?: string | number | string[] | number[]
  getOptionValue?: (option: any) => any
  getOptionLabel?: (option: any) => any
  isNone?: boolean
  noneText?: string
  NoOptionsMessage?: string
  onChange?: (data: string | number | string[] | number[]) => void
  // onChange?: (data: { [key: string]: string | number }) => void
  isTooltip?: boolean
  tooltipText?: string
  small?: boolean
  color?: 'blue'
  selectType?: SelectType
  flexDirection?: FlexDirectionType
  labelColor?: LabelColorType
  alignItems?: alignItemsType
  closeMenuOnSelect?: boolean
  onMenuClose?: () => void
  isLoading?: boolean
}

export const Select: FC<SelectSchema> = ({
  uid = 'select-' + uuidv4(),
  label,
  name,
  required,
  errors,
  placeholder,
  disabled,
  className,
  classNameLabel,
  options = [],
  optionValue = 'value',
  optionLabel = 'label',
  optionImageUrl = '',
  optionImageAlt = '',
  isMulti,
  isSearchable,
  noOptionsMessage,
  control,
  getOptionValue,
  getOptionLabel,
  isNone,
  noneText = 'None',
  onChange,
  defaultValue,
  color,
  small,
  selectType,
  flexDirection = 'column',
  labelColor = 'dark',
  alignItems = 'left',
  closeMenuOnSelect = true,
  onMenuClose,
  isLoading,
}) => {
  const [data, setData] = useState<OptionsType>([])

  const [
    ,
    //tooltipVisible
    setTooltipVisible,
  ] = useState<boolean>(false)
  const tooltipId = 'select-tooltip' + uuidv4()

  useEffect(() => {
    if (options.length) {
      // const arr = options.map(option => {
      //   const el = Object.assign({}, option)
      //   el[optionLabel] = t(el[optionLabel])

      //   return el
      // })

      setData(options)
    } else {
      setData([])
    }
  }, [options])

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        {/* <Icon name='chevron-down' fill={color === 'blue' ? 'white' : 'black'} /> */}
      </components.DropdownIndicator>
    )
  }

  const Menu = (
    props: any, //MenuProps
  ): JSX.Element => {
    return (
      // <div className={cnSelect('menu')}>
      <components.Menu {...props}>{props.children}</components.Menu>
      // </div>
    )
  }

  const Option = (props: any): JSX.Element => {
    const imageUrl = handleOptionImageUrl(props.data)
    const imageAlt = handleOptionImageAlt(props.data)

    const image = imageUrl ? <img src={imageUrl} alt={imageAlt} /> : null

    return (
      <components.Option {...props}>
        <div className={cnSelect('option-container')}>
          {image}
          {props.children}
        </div>
      </components.Option>
    )
  }

  const SingleValue = (props: any): JSX.Element => {
    const imageUrl = handleOptionImageUrl(props.data)
    const imageAlt = handleOptionImageAlt(props.data)

    const image = imageUrl ? <img src={imageUrl} alt={imageAlt} /> : null

    return (
      <components.SingleValue {...props}>
        <div className={cnSelect('option-container')}>
          {image}
          {props.children}
        </div>
      </components.SingleValue>
    )
  }

  const handleOptionValue = (option: any) => {
    return optionValue && option[optionValue]
  }

  const handleOptionLabel = (option: any) => {
    return optionLabel && option[optionLabel]
  }

  const handleOptionImageUrl = (option: any) => {
    return optionImageUrl && option[optionImageUrl]
  }

  const handleOptionImageAlt = (option: any) => {
    return optionImageAlt && option[optionImageAlt]
  }

  const generateOption = (
    key: string,
    value: string | number | null,
  ): OptionType => {
    return { [key]: value }
  }

  useEffect(() => {
    if (isNone && options.length) {
      const label = generateOption(optionLabel, noneText)
      const value = generateOption(optionValue, null)

      setData([
        {
          ...label,
          ...value,
        },
        ...options,
      ])
    }
  }, [options, isNone])

  const handleChange = (e: string | number | string[] | number[]): void => {
    onChange && onChange(e)
  }

  const currentValue = (value: any | Array<any>): any | any[] => {
    if (!optionValue && !value) return

    if (isMulti) {
      let ops: string[] | number[] = []

      if (!Array.isArray(value)) {
        ops = [value]
      } else {
        ops = value
      }

      const arr = data.filter(el => {
        const val: string | number = el[optionValue]

        return ops.includes(val as never)
      })

      return arr
    }

    if (!data.length) {
      return null
    }

    return data.find(el => el[optionValue] === value)
  }

  return (
    <>
      <div
        className={cnSelect(
          {
            error: !!errors,
            blue: color === 'blue',
            small,
            simple: selectType === 'simple',
            filter: selectType === 'filter',
            column: flexDirection === 'column',
            row: flexDirection === 'row',
            top: alignItems === 'top',
            bottom: alignItems === 'bottom',
            left: alignItems === 'left',
            right: alignItems === 'right',
            middle: alignItems === 'middle',
          },
          [className],
        )}
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
        id={tooltipId}
      >
        {!!label && (
          <label
            htmlFor={uid}
            className={cnSelect(
              'label',
              {
                required,
                dark: labelColor === 'dark',
                blue: labelColor === 'blue',
                filter: selectType === 'filter',
              },
              [classNameLabel, required ? 'text' : undefined],
            )}
          >
            <div className={cnSelect('text', { required })}>{label}</div>
          </label>
        )}
        <div className={cnSelect('wrap')}>
          {control ? (
            <Controller
              name={name}
              control={control}
              render={({ field: { onChange, value } }) => (
                <SelectBox
                  classNamePrefix={cnSelect()}
                  // inputRef={ref}
                  value={currentValue(value)}
                  options={data}
                  onChange={(option: any | any[]) => {
                    if (isMulti) {
                      const arr = option.map((el: any) => el[optionValue])
                      onChange(arr)
                    } else {
                      onChange(option[optionValue])
                    }
                  }}
                  isMulti={isMulti}
                  isSearchable={isSearchable}
                  noOptionsMessage={noOptionsMessage}
                  placeholder={placeholder}
                  isDisabled={disabled}
                  components={{ DropdownIndicator, Menu, Option, SingleValue }}
                  getOptionValue={
                    getOptionValue ? getOptionValue : handleOptionValue
                  }
                  getOptionLabel={
                    getOptionLabel ? getOptionLabel : handleOptionLabel
                  }
                  // NoOptionsMessage={NoOptionsMessage}
                  // menuIsOpen={true}
                  closeMenuOnSelect={closeMenuOnSelect}
                  onMenuClose={onMenuClose}
                  isLoading={isLoading}
                />
              )}
            />
          ) : (
            <SelectBox
              classNamePrefix={cnSelect()}
              value={currentValue(defaultValue)}
              options={data}
              onChange={(option: any | any[]) => {
                if (isMulti) {
                  const arr = option.map((el: any) => el[optionValue])
                  handleChange(arr)
                } else {
                  handleChange(option[optionValue])
                }
              }}
              isMulti={isMulti}
              isSearchable={isSearchable}
              noOptionsMessage={noOptionsMessage}
              placeholder={placeholder}
              isDisabled={disabled}
              components={{ DropdownIndicator, Menu, Option, SingleValue }}
              getOptionValue={
                getOptionValue ? getOptionValue : handleOptionValue
              }
              getOptionLabel={
                getOptionLabel ? getOptionLabel : handleOptionLabel
              }
              // NoOptionsMessage={NoOptionsMessage}
              closeMenuOnSelect={closeMenuOnSelect}
              onMenuClose={onMenuClose}
              isLoading={isLoading}
            />
          )}

          <Transition in={!!errors} timeout={500} mountOnEnter unmountOnExit>
            {state => (
              <div className={cnSelect('error', [state])}>{errors}</div>
            )}
          </Transition>
        </div>
      </div>

      {/* {isTooltip && !!tooltipText && (
        <Tooltip
          target={'#' + tooltipId}
          visible={tooltipVisible}
          closeOnOutsideClick={false}
        >
          <div>{tooltipText}</div>
        </Tooltip>
      )} */}
    </>
  )
}
